import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, X } from "lucide-react";
import { can, useOrg } from "../org";
import { CareState, Initials, REL, Rhythm, Select, SourceTag, displayName, fromPco } from "./bits";
import { PersonForm } from "./PersonForm";
import { CaregiverDialog, HouseholdDialog } from "./Dialogs";
import { PersonCare } from "../care/PersonCare";
import { Confidential } from "../care/Confidential";
import { PersonAccess } from "./Access";
import { PersonMoments } from "../care/LifeMoments";
import { lastText } from "../care/text";

const Section = ({ title, action, children, testid }) => (
  <section className="py-7 border-t border-line" data-testid={testid}>
    <div className="flex items-center justify-between gap-3 mb-3"><h2 className="t-label">{title}</h2>{action}</div>
    {children}
  </section>
);

const Household = ({ p, c, onManage }) => (
  <Section title="Household" testid="person-household" action={c.directory && p.household && !fromPco(p.household) && <button onClick={onManage} className="btn btn-quiet btn-sm" data-testid="person-household-manage">Manage</button>}>
    {p.household ? (
      <>
        <p className="t-name-sm flex items-center gap-2" data-testid="person-household-name">{p.household.name}<SourceTag x={p.household} testid="person-household-source" /></p>
        <p className="t-meta">{displayName(p)} · {REL[p.household_relationship] || "Member"}</p>
        <div className="mt-3 space-y-2">
          {p.household_members?.map((m) => (
            <Link key={m.id} to={`/people/${m.id}`} className="flex justify-between gap-3 py-2 hover:underline" data-testid={`person-household-member-${m.id}`}>
              <span className="t-body">{m.preferred_name || m.first_name} {m.last_name}</span>
              <span className="t-meta">{REL[m.relationship] || "Member"}</span>
            </Link>
          ))}
          {p.household_hidden_count > 0 && <p className="t-meta" data-testid="person-household-hidden">and {p.household_hidden_count} other{p.household_hidden_count > 1 ? "s" : ""} you don't care for</p>}
        </div>
      </>
    ) : <p className="t-meta">Not part of a household yet.</p>}
  </Section>
);

const Groups = ({ p, c, reload }) => {
  const { api } = useOrg();
  const [all, setAll] = useState([]);
  useEffect(() => { if (c.directory) api("/groups").then(setAll).catch(() => {}); }, [api, c.directory]);
  const act = (prom, msg) => prom.then(() => { toast.success(msg); reload(); }).catch((e) => toast.error(e.message));
  const mine = new Set(p.groups.map((g) => g.id));
  return (
    <Section title="Care Groups" testid="person-groups">
      <div className="flex flex-wrap gap-2">
        {p.groups.map((g) => (
          <span key={g.id} className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-sunk text-[15px]" data-testid={`person-group-${g.id}`}>
            <Link to={`/groups/${g.id}`} className="hover:underline">{g.name}</Link>{g.rhythm_days && <span className="t-meta">{g.rhythm_days}d</span>}
            {c.directory && !fromPco(g) && <button onClick={() => act(api(`/groups/${g.id}/members/${p.id}`, { method: "DELETE" }), `Removed from ${g.name}`)} aria-label={`Remove from ${g.name}`} className="-my-2 -mr-2 p-2.5" data-testid={`person-group-remove-${g.id}`}><X size={14} /></button>}
          </span>
        ))}
        {!p.groups.length && <p className="t-meta">Not in any Care Group.</p>}
      </div>
      {c.directory && (
        <Select value="" onChange={(id) => id && act(api(`/groups/${id}/members`, { method: "POST", body: { person_id: p.id } }), "Added to group")} testid="person-group-add" className="mt-4 !w-auto">
          <option value="">+ Add to a group</option>
          {all.filter((g) => !mine.has(g.id) && !fromPco(g)).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </Select>
      )}
    </Section>
  );
};

export default function Person() {
  const { id } = useParams();
  const { api, membership } = useOrg();
  const c = can(membership);
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => api(`/people/${id}`).then(setP).catch((e) => setErr(e.message));
  useEffect(() => { setP(null); setErr(""); load(); }, [id, api]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const on = () => load(); window.addEventListener("care:logged", on); return () => window.removeEventListener("care:logged", on); }, [id, api]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = () => { setOpen(null); load(); };
  if (err) return <div data-testid="person-not-found"><p className="t-greeting">We couldn't find that person.</p><p className="t-lead mt-3">They may not be someone you care for.</p><Link to="/people" className="btn btn-primary mt-8">Back to People</Link></div>;
  if (!p) return <p className="t-meta">Loading…</p>;
  return (
    <div data-testid="person-screen">
      <Link to="/people" className="inline-flex items-center gap-2 py-3 -my-3 t-meta hover:text-ink" data-testid="person-back"><ArrowLeft size={16} /> People</Link>
      <div className="flex items-start gap-5 mt-6">
        <Initials p={p} size={84} />
        <div className="min-w-0 flex-1">
          <h1 className="t-name-xl [overflow-wrap:anywhere]" data-testid="person-name">{displayName(p)}</h1>
          <p className="t-lead mt-2" data-testid="person-subline">{[p.household?.name].filter(Boolean).join(" · ")}</p>
          {fromPco(p) && <p className="mt-2 flex items-center gap-2"><SourceTag x={p} testid="person-source" />{(p.pco_status === "inactive" || p.pco_included === false) && <span className="t-meta" data-testid="person-pco-inactive">{p.pco_status === "inactive" ? "Inactive in Planning Center" : "No longer included by Planning Center"}{p.pco_keep_monitored ? " · still monitored" : ""}</span>}
            {(p.pco_status === "inactive" || p.pco_included === false) && c.care && <button onClick={() => api(`/people/${p.id}/monitoring`, { method: "PATCH", body: { keep: !p.pco_keep_monitored } }).then((x) => { setP({ ...p, ...x }); toast.success(x.pco_keep_monitored ? "Care will keep monitoring them" : "Moved out of routine monitoring"); }).catch((e) => toast.error(e.message))} className="btn btn-quiet btn-sm" data-testid="person-monitoring-toggle">{p.pco_keep_monitored ? "Stop monitoring" : "Keep monitoring"}</button>}</p>}
          {c.seesCare && <p className="t-meta mt-1" data-testid="person-last-connection">{lastText(p.last_connection)}</p>}
          {c.seesCare && <div className="mt-3"><CareState state={p.care_state} testid="person-care-state" /></div>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-6">
        {!c.seesCare && p.phone && <a href={`tel:${p.phone}`} className="btn btn-quiet btn-sm" data-testid="person-call"><Phone size={16} /> Call</a>}
        {p.email && <a href={`mailto:${p.email}`} className="btn btn-quiet btn-sm" data-testid="person-email"><Mail size={16} /> Email</a>}
        {c.directory && <button onClick={() => setOpen("edit")} className="btn btn-outline btn-sm" data-testid="person-edit-btn">Edit details</button>}
      </div>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-x-14 mt-4">
        <div className="min-w-0">{c.seesCare && <PersonCare p={p} />}{c.care && <Confidential p={p} />}</div>
        <div className={c.seesCare ? "lg:mt-6" : "mt-6"}>
        <Section title="Assigned Care Leader" testid="person-caregiver" action={c.oversee && <button onClick={() => setOpen("caregiver")} className="btn btn-quiet btn-sm" data-testid="person-assign-btn">{p.caregiver ? "Reassign" : "Assign"}</button>}>
          {p.caregiver ? <p className="t-name-sm" data-testid="person-caregiver-name">{p.caregiver.name}</p> : <p className="status status-attention" data-testid="person-caregiver-none">No Assigned Care Leader yet</p>}
        </Section>
        {c.seesCare && <Section title="Care Rhythm" testid="person-rhythm-section"><Rhythm r={p.rhythm} testid="person-rhythm" /></Section>}
        <Household p={p} c={c} onManage={() => setOpen("household")} />
        <Groups p={p} c={c} reload={load} />
        <PersonMoments p={p} c={c} Section={Section} reload={load} />
        {c.manage && <PersonAccess p={p} Section={Section} />}
        </div>
      </div>
      {open === "edit" && <PersonForm person={p} onClose={() => setOpen(null)} onSaved={done} />}
      {open === "caregiver" && <CaregiverDialog person={p} onClose={() => setOpen(null)} onSaved={done} />}
      {open === "household" && <HouseholdDialog person={p} onClose={() => setOpen(null)} onSaved={done} />}
    </div>
  );
}
